import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUpDown, CalendarDays, Check, ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@workspace/ui/components/ui/drawer";
import { cn } from "@/lib/utils";

/**
 * Mobile-web (< lg) search chrome: compact search pill + filter badge,
 * one horizontal chip rail, and quick-filter bottom sheets. Desktop
 * never renders these (every root is `lg:hidden`).
 */

export function formatSearchDateRange(from: string | undefined, to: string | undefined, locale: string): string | null {
  if (!from || !to) return null;
  const parse = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  };
  const a = parse(from);
  const b = parse(to);
  if (!a || !b) return null;
  try {
    const fmt = new Intl.DateTimeFormat(locale || "en", { day: "numeric", month: "short" });
    return `${fmt.format(a)} – ${fmt.format(b)}`;
  } catch {
    return `${from} – ${to}`;
  }
}

export interface MobileChip {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
  /** Opens a sheet (shows a chevron) rather than toggling in place. */
  opensSheet?: boolean;
  icon?: ReactNode;
}

export function MobileSearchHeader({
  locationLabel,
  datesLabel,
  filterCount,
  onOpenSearch,
  onOpenFilters,
  chips,
}: {
  locationLabel: string | null;
  datesLabel: string | null;
  filterCount: number;
  onOpenSearch: () => void;
  onOpenFilters: () => void;
  chips: MobileChip[];
}) {
  const { t } = useTranslation();
  return (
    <div className="msh-root sticky top-[var(--header-h)] z-40 -mx-4 mb-4 px-4 pb-2.5 pt-3 sm:-mx-6 sm:px-6 lg:hidden" data-testid="search-mobile-header">
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onOpenSearch}
          className="msh-pill flex min-h-[52px] min-w-0 flex-1 items-center gap-3 rounded-full px-4 text-left"
          aria-label={t("search.mobile.edit_search", { defaultValue: "Edit location and dates" })}
        >
          <Search className="h-[18px] w-[18px] shrink-0 text-primary" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[0.9375rem] font-semibold leading-tight text-foreground">
              {locationLabel || t("search.mobile.anywhere", { defaultValue: "Anywhere" })}
            </span>
            <span className="mt-0.5 block truncate text-xs leading-tight text-muted-foreground">
              {datesLabel || t("search.mobile.any_dates", { defaultValue: "Any dates" })}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={onOpenFilters}
          className="msh-filter relative flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full"
          aria-label={
            filterCount > 0
              ? t("search.mobile.filters_with_count", { defaultValue: "Filters, {{count}} applied", count: filterCount })
              : t("common.filters")
          }
          data-testid="search-mobile-filters-button"
        >
          <SlidersHorizontal className="h-5 w-5" aria-hidden />
          {filterCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground ring-2 ring-background" aria-hidden>
              {filterCount}
            </span>
          )}
        </button>
      </div>
      <div className="msh-rail -mx-4 mt-2.5 flex gap-2 overflow-x-auto px-4 scrollbar-hide sm:-mx-6 sm:px-6" role="toolbar" aria-label={t("search.mobile.quick_filters", { defaultValue: "Quick filters" })}>
        {chips.map((chip) => (
          <button
            key={chip.id}
            type="button"
            onClick={chip.onClick}
            aria-pressed={chip.opensSheet ? undefined : chip.active}
            aria-haspopup={chip.opensSheet ? "dialog" : undefined}
            className={cn("msh-chip inline-flex min-h-[40px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm font-medium", chip.active && "is-active")}
          >
            {chip.icon}
            {chip.label}
            {chip.opensSheet && <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />}
          </button>
        ))}
      </div>
    </div>
  );
}

export function MobileQuickSheet({
  open,
  onOpenChange,
  title,
  children,
  onClear,
  clearDisabled,
  showLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  onClear?: () => void;
  clearDisabled?: boolean;
  showLabel: string;
}) {
  const { t } = useTranslation();
  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent className="msq-sheet lg:hidden max-h-[85vh]" data-testid="search-mobile-quick-sheet">
        <DrawerDescription className="sr-only">{title}</DrawerDescription>
        <div className="flex items-start justify-between px-6 pb-2 pt-4">
          <DrawerTitle className="msq-title text-[1.375rem] font-semibold tracking-tight text-foreground">{title}</DrawerTitle>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="-mr-2 -mt-1 inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground hover:bg-muted"
            aria-label={t("common.close", { defaultValue: "Close" })}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="search-mobile-filter-body min-h-0 flex-1 overflow-y-auto px-6 pb-5 pt-2">{children}</div>
        <MobileSheetFooter onClear={onClear} clearDisabled={clearDisabled} clearLabel={t("search.mobile.clear", { defaultValue: "Clear" })} showLabel={showLabel} onShow={() => onOpenChange(false)} />
      </DrawerContent>
    </Drawer>
  );
}

export function MobileSheetFooter({
  onClear,
  clearDisabled,
  clearLabel,
  showLabel,
  onShow,
}: {
  onClear?: () => void;
  clearDisabled?: boolean;
  clearLabel: string;
  showLabel: string;
  onShow: () => void;
}) {
  return (
    <div className="search-mobile-filter-footer msq-footer flex shrink-0 items-center justify-between gap-4 border-t border-border bg-background px-6 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      {onClear ? (
        <button
          type="button"
          onClick={onClear}
          disabled={clearDisabled}
          className="min-h-11 px-1 text-[0.9375rem] font-semibold text-foreground underline underline-offset-4 disabled:text-muted-foreground/60 disabled:no-underline"
        >
          {clearLabel}
        </button>
      ) : <span />}
      <button
        type="button"
        onClick={onShow}
        className="min-h-[48px] rounded-xl bg-primary px-6 text-[0.9375rem] font-semibold text-primary-foreground shadow-sm active:scale-[0.98] transition-transform"
        data-testid="search-mobile-show-results"
      >
        {showLabel}
      </button>
    </div>
  );
}

/** Airbnb-style "Selected" pills for the full filter sheet. */
export function MobileSelectedPills({
  items,
  onRemove,
}: {
  items: Array<{ key: string; value: string; label: string; display?: string; isMulti?: boolean }>;
  onRemove: (key: string, value: string, isMulti?: boolean) => void;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <section className="msf-selected" aria-label={t("search.mobile.selected", { defaultValue: "Selected" })}>
      <h3 className="msf-heading">{t("search.mobile.selected", { defaultValue: "Selected" })}</h3>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.map((f, i) => (
          <button
            key={`${f.key}-${f.value}-${i}`}
            type="button"
            onClick={() => onRemove(f.key, f.value, f.isMulti)}
            aria-label={t("search.remove_filter_aria", { defaultValue: "Remove {{label}} filter", label: f.label })}
            className="msf-pill inline-flex min-h-[40px] max-w-full items-center gap-2 rounded-full px-3.5 text-left text-sm font-medium"
          >
            <span className="min-w-0 truncate">{f.label}: {f.display ?? f.value}</span>
            <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
          </button>
        ))}
      </div>
    </section>
  );
}

/** Radio list used by the Sort quick sheet. */
export function MobileSortOptions({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div role="radiogroup" className="-mx-2">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={cn("flex min-h-[52px] w-full items-center justify-between rounded-xl px-2 text-left text-[0.9375rem]", selected ? "font-semibold text-foreground" : "text-foreground/85")}
          >
            {opt.label}
            <span className={cn("flex h-6 w-6 items-center justify-center rounded-full border", selected ? "border-primary bg-primary text-primary-foreground" : "border-border")}>
              {selected && <Check className="h-3.5 w-3.5" aria-hidden />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export const mobileChipIcons = {
  sort: <ArrowUpDown className="h-3.5 w-3.5" aria-hidden />,
  dates: <CalendarDays className="h-3.5 w-3.5" aria-hidden />,
};
